import { ReactNode, useEffect, useMemo } from 'react';
import { StyleSheet, View, Pressable } from 'react-native';
import { theme, Icon, styles as baseStyles } from '@socrait/react';
import RenderHtml, { MixedStyleRecord } from 'react-native-render-html';
import { marked } from 'marked';

import { CopyButton } from './CopyButton';
import { CopyText } from './AccordionTypes';

export type AccordionState = 'initial' | 'loading' | 'loaded' | 'expanded';

/**
 * AccordionBase component supports three states:
 * - initial: Shows a button that says "Draft message to parent" or "View" depending on the type
 * - loading: Shows a loading spinner while the request is pending
 * - loaded: Shows a "Review" button when loaded (DataLogs will auto-expand)
 * - expanded: The accordion is expanded to show the content
 */
interface AccordionBaseProps {
  title: string;
  icon: ReactNode;
  rightControls?: ReactNode;
  /** Rendered above the (optionally bordered) content area, e.g. a subject pill switcher. */
  aboveContent?: ReactNode;
  content?: ReactNode;
  copyText?: string | CopyText;
  extraActions?: ReactNode;
  state?: AccordionState;
  autoExpand?: boolean;
  onReviewButtonClick?: () => void;
  isCollapsed?: boolean;
  onToggleCollapse: (isCollapsed: boolean) => void;
  noContentBorder?: boolean;
  containerStyle?: object;
}

export const AccordionBase = ({
  title,
  icon,
  rightControls,
  aboveContent,
  content,
  copyText,
  extraActions,
  state,
  autoExpand = false,
  onReviewButtonClick,
  isCollapsed,
  onToggleCollapse,
  noContentBorder = false,
  containerStyle,
}: AccordionBaseProps) => {
  const titleHtml = useMemo(() => marked.parseInline(title) as string, [title]);

  // Auto-expand DataLogs when the content is loaded
  useEffect(() => {
    if (state === 'loaded' && autoExpand && onReviewButtonClick) {
      onReviewButtonClick();
    }
  }, [state, autoExpand, onReviewButtonClick]);

  // Render expanded content with copy button
  const renderExpandedContent = () => {
    return (
      <View style={styles.contentContainer}>
        {aboveContent}
        <View style={[styles.contentArea, noContentBorder && styles.noContentBorder]}>{content}</View>
        {(copyText !== undefined || extraActions) && (
          <View style={styles.actionsRow}>
            <View style={styles.actionsLeft}>{extraActions}</View>
            {copyText !== undefined && <CopyButton text={copyText} />}
          </View>
        )}
      </View>
    );
  };

  return (
    <View style={[styles.container, !isCollapsed && styles.expandedContainer, containerStyle]}>
      <View style={styles.header}>
        <View style={styles.leftContainer}>
          <View style={styles.iconContainer}>{icon}</View>
          <View style={styles.titleContainer}>
            <RenderHtml
              source={{ html: titleHtml }}
              baseStyle={styles.title}
              tagsStyles={titleTagStyles}
              systemFonts={titleFonts}
              defaultTextProps={{ numberOfLines: 1, ellipsizeMode: 'tail' }}
            />
          </View>
        </View>

        {isCollapsed ? (
          <View style={styles.rightContainer}>{rightControls}</View>
        ) : (
          <View style={styles.topArrow}>
            <Pressable onPress={() => onToggleCollapse(true)}>
              <Icon type="topArrow" />
            </Pressable>
          </View>
        )}
      </View>

      {!isCollapsed && renderExpandedContent()}
    </View>
  );
};

const titleFonts = [theme.fonts.regular];

const titleTagStyles: MixedStyleRecord = {
  body: { fontFamily: theme.fonts.regular },
};

export const styles = StyleSheet.create({
  container: {
    height: 60,
    minWidth: 220,
    padding: 2,
    borderRadius: 6,
    backgroundColor: theme.colors.white,
    borderWidth: 2,
    borderColor: theme.colors.gray,
    display: 'flex',
    justifyContent: 'center',
  },
  expandedContainer: {
    height: 'auto',
    paddingTop: theme.spacing.medium,
  },
  header: {
    paddingVertical: theme.spacing.medium,
    paddingHorizontal: theme.spacing.xlarge,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  leftContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.medium,
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
  },
  iconContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 24,
    height: 24,
    flexShrink: 0,
  },
  titleContainer: {
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
  },
  rightContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing.medium,
    paddingVertical: theme.spacing.xsmall,
    flexShrink: 0,
  },
  topArrow: {
    paddingHorizontal: theme.spacing.xsmall,
  },
  title: {
    ...baseStyles.title,
    fontSize: theme.fontSizes.small,
    fontStyle: 'normal',
    lineHeight: 20,
  },
  contentContainer: {
    display: 'flex',
    gap: theme.spacing.xlarge,
    paddingHorizontal: theme.spacing.xlarge,
    paddingTop: theme.spacing.medium,
    paddingBottom: theme.spacing.xlarge,
  },
  contentArea: {
    borderWidth: 1,
    borderColor: theme.colors.darkGray,
    maxHeight: 400,
  },
  noContentBorder: {
    borderWidth: 0,
  },
  actionsRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing.medium,
  },
  actionsLeft: {
    flex: 1,
    flexShrink: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.medium,
  },
});
